import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight, ExternalLink } from "lucide-react";
import { VsMark } from "@/components/Logo";
import { COMPANY_URL } from "@/components/Seo";

const NAV = [
  ["Platform", "/platform"], ["Solutions", "/solutions"], ["Clients", "/clients"],
  ["Vendors", "/vendors"], ["Staffing", "/staffing-partners"], ["Products", "/products"], ["About", "/about"],
];

export function PublicHeader() {
  const [open, setOpen] = React.useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="public-brand">
          <VsMark />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-vs-ink text-[15px]">VS PartnerHub</div>
            <div className="text-[10px] text-slate-500">by Vijay Software Solutions</div>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map(([label, to]) => (
            <NavLink key={to} to={to} data-testid={`public-nav-${to.slice(1)}`}
              className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-vs-royal bg-vs-light" : "text-slate-600 hover:text-vs-royal"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-vs-royal px-3 py-2" data-testid="public-login">Sign In</Link>
          <Link to="/register" className="inline-flex items-center gap-1 rounded-lg bg-vs-royal px-4 py-2 text-sm font-semibold text-white hover:bg-vs-blue transition-colors" data-testid="public-register">
            Partner Registration<ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="public-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1" aria-label="Mobile">
          {NAV.map(([label, to]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">{label}</Link>
          ))}
          <div className="flex gap-2 pt-2">
            <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">Sign In</Link>
            <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center rounded-lg bg-vs-royal px-4 py-2 text-sm font-semibold text-white">Register</Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function PublicFooter() {
  const cols = [
    { title: "Platform", links: [["Platform Overview", "/platform"], ["Solutions", "/solutions"], ["Products", "/products"], ["Resources", "/resources"]] },
    { title: "Workspaces", links: [["For Clients", "/clients"], ["For Vendors", "/vendors"], ["For Staffing Partners", "/staffing-partners"], ["Partner Registration", "/register"]] },
    { title: "Company", links: [["About VS-Solutions", "/about"], ["Contact", "/contact"], ["Vijay Software Solutions", COMPANY_URL, true]] },
  ];
  return (
    <footer className="bg-vs-deep text-slate-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2.5">
            <VsMark tile />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white">VS PartnerHub</div>
              <div className="text-[10px] text-slate-400">Enterprise Partner Management Platform</div>
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-4 leading-relaxed">
            VS PartnerHub is the enterprise partner management platform by Vijay Software Solutions Pvt Ltd (VS-Solutions),
            connecting clients, vendors and staffing partners through procurement, staffing, agreements and secure business workflows.
          </p>
          <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-300 hover:text-blue-200" data-testid="footer-company-link">
            Vijay Software Solutions Pvt Ltd — vs-solutions.in<ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">{c.title}</div>
            <ul className="space-y-2">
              {c.links.map(([label, to, ext]) => (
                <li key={label}>
                  {ext ? (
                    <a href={to} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1">{label}<ExternalLink className="h-3 w-3" /></a>
                  ) : (
                    <Link to={to} className="text-sm text-slate-400 hover:text-white">{label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Vijay Software Solutions Pvt Ltd. All rights reserved.</span>
          <span>VS PartnerHub · A VS-Solutions platform · vs-partnerhub.com</span>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
}
